import { useMemo } from 'react';
import type { EnrollmentItem, QueueResponse, SenderOption } from '@shared/flow/enrollment';

import type { DefaultSenderState } from '../../EnrollQueue/enroll-queue-types';
import type { CopyPrefill } from '../../EnrollQueue/EnrollmentItemEditor';
import { deriveContactQueueEntries, type ContactQueueEntry } from '../../EnrollQueue/queue-derive';
import { deriveGroupSendability } from '../../EnrollQueue/queue-group-send';
import {
  mergeStagedCopyPrefill,
  playConfigKey,
  useStagedBaseCopy,
  useStagedBaseSender,
  useStagedSenderTouched,
  useStagedStepCopy,
} from '../../EnrollQueue/state/staged-play-config';
import { LeadRow } from './LeadRow';
import { matchesQueueFilter } from './leadDisplay';
import { NoQueueMatches } from './NoQueueMatches';
import type { PlaySectionSharedProps, QueuePaneViewProps } from './paneCommon';

/**
 * The Plays pane's By Contact view (plan 2026-09-30-002, D16/D23/D30, E65/E66)
 * — the SAME `groups` data, re-projected to one row per contact.
 *
 * Ordering and de-duplication are `deriveContactQueueEntries`' job, imported
 * from `QueueBody` and never re-implemented (D30): newest-added first with an
 * id DESC tiebreak, and the "one open item per contact" invariant guard. D23
 * — no recency buckets, no per-play grouping: it is one flat list.
 */
export function ByContact({ groups, q, sendersFor, defaultSenderFor, section }: QueuePaneViewProps) {
  const entries = useMemo(
    () => deriveContactQueueEntries(groups).filter(entry => matchesQueueFilter(entry.item, entry.playName, q)),
    [groups, q],
  );
  /** `${play_type_id}:${play_instance_ref}` → group, so each row can resolve its own play's sendability and staged copy. */
  const groupByKey = useMemo(() => {
    const map = new Map<string, QueueResponse['groups'][number]>();
    for (const group of groups) map.set(`${group.play_type_id}:${group.play_instance_ref}`, group);
    return map;
  }, [groups]);

  if (entries.length === 0) {
    // Same split as `ByPlay`'s (E66): "No matches" is a statement about the
    // filter, so with no filter there is nothing to say here at all.
    return q === '' ? null : <NoQueueMatches q={q} />;
  }

  return (
    <div className="flex flex-col gap-2">
      {entries.map(entry => {
        const group = groupByKey.get(`${entry.item.play_type_id}:${entry.item.play_instance_ref}`);
        // `deriveContactQueueEntries` only ever emits items it read OUT of
        // `groups`, so this lookup cannot miss; the guard is a type narrow,
        // not a real branch.
        if (!group) return null;
        return (
          <ContactLeadRow
            key={entry.item.id}
            entry={entry}
            group={group}
            senders={sendersFor(entry.item.play_type_id)}
            defaultSender={defaultSenderFor(entry.item.play_type_id)}
            section={section}
          />
        );
      })}
    </div>
  );
}

interface ContactLeadRowProps {
  entry: ContactQueueEntry;
  group: QueueResponse['groups'][number];
  senders: SenderOption[];
  defaultSender: DefaultSenderState;
  section: PlaySectionSharedProps;
}

/**
 * One By Contact row, and the reason this view needs a per-row component at
 * all: the staged-play-config reads (`useStagedBaseCopy`, `useStagedBaseSender`,
 * `useStagedSenderTouched`, `useStagedStepCopy`) are HOOKS, and By Contact's
 * list spans several plays — so they cannot be hoisted into `ByContact`'s own
 * body without calling hooks in a loop. `PlaySection` gets to do exactly the
 * same derivations once per section because a section IS one play.
 *
 * Every derivation below is `PlayQueueCard`'s/`PlaySection`'s, verbatim, on
 * purpose: a row must show the SAME pill, the same effective sender and the
 * same base copy in both groupings. In particular `deriveGroupSendability`
 * runs over the row's whole GROUP (not the single item), because block labels
 * are a group-level derivation — running it per-item would silently change
 * what a rep sees the moment they flip the grouping toggle.
 */
function ContactLeadRow({ entry, group, senders, defaultSender, section }: ContactLeadRowProps) {
  const item = entry.item;
  const playTypeId = group.play_type_id as number;
  const playInstanceRef = group.play_instance_ref as string;
  const play = section.plays.find(p => p.play_type_id === playTypeId && p.play_instance_ref === playInstanceRef);

  const identity = section.baseCopyDraftStore.identity;
  const playKey = playConfigKey(playTypeId, playInstanceRef);
  const baseDraft = useStagedBaseCopy(identity, playKey);
  const copyPrefill: CopyPrefill = useMemo(() => mergeStagedCopyPrefill(baseDraft, {
    subject: play?.invite_subject ?? null,
    body: play?.invite_body ?? null,
  }), [baseDraft, play?.invite_subject, play?.invite_body]);
  const stagedSender = useStagedBaseSender(identity, playKey);
  const senderTouchedIds = useStagedSenderTouched(identity);
  const stagedStepCopy = useStagedStepCopy(identity, playKey);

  const senderPrefillFor = (target: EnrollmentItem): string | null =>
    senderTouchedIds.includes(target.id) ? null : stagedSender ?? null;
  const effectiveSenderFor = (target: EnrollmentItem): string | null =>
    senderPrefillFor(target) ?? target.sender;
  const baseSender = stagedSender
    ?? (defaultSender.resolved ? defaultSender.sender : item.sender)
    ?? null;

  const { blockLabels } = deriveGroupSendability(group.items, section.sendReadOnly ?? section.readOnly, section.sendSkipLabelFor, effectiveSenderFor);
  const index = group.items.findIndex(candidate => candidate.id === item.id);
  const blockLabel = index === -1 ? null : blockLabels[index];

  return (
    <LeadRow
      item={item}
      // D16 — By Contact is the ONE view that folds the play name into the
      // meta line; By Play's section header already names it.
      playName={entry.playName}
      blockLabel={blockLabel}
      expanded={section.expandedItemId === item.id}
      onToggleExpanded={section.onToggleExpanded}
      readOnly={section.readOnly}
      sendReadOnly={section.sendReadOnly ?? section.readOnly}
      isItemBusy={section.isItemBusy}
      handlers={section.handlers}
      identity={identity}
      baseSender={baseSender}
      sending={section.sending || section.handlers.groupSendPending}
      senders={senders}
      plays={section.plays}
      activePlayTypes={section.activePlayTypes}
      gongFlowsSuggested={section.gongFlowsSuggested}
      onReassign={section.onReassign}
      onClearStepOverrides={section.onClearStepOverrides}
      requestQueueFlash={section.requestQueueFlash}
      onRemove={section.onRemove}
      inputOptions={section.inputOptionsFor?.(item.id)}
      onSelectInputOption={section.onSelectInputOption}
      onRefreshInputOptionsIfStale={section.onRefreshInputOptionsIfStale}
      copyPrefill={copyPrefill}
      senderPrefill={senderPrefillFor(item)}
      onSenderTouched={section.onSenderTouched}
      // Group-sourced capability data, resolved by `deriveContactQueueEntries`
      // while the group was still in hand — see its own doc comment for why
      // `plays.find` cannot answer for a `catalog: true` exposure kind.
      hasEditableCopy={entry.hasEditableCopy}
      copyRequired={entry.copyRequired}
      onAutosave={section.onAutosave}
      onFlushSave={section.onFlushSave}
      gongFlowId={entry.gongFlowId}
      bodyFormat={entry.bodyFormat}
      onEditStepOverride={section.onEditStepOverride}
      onResetStepOverride={section.onResetStepOverride}
      stepOverrideDraftFor={section.stepOverrideDraftFor}
      stepSaveErrorFor={section.stepSaveErrorFor}
      subscribeStepOverrides={section.subscribeStepOverrides}
      onSaveStepOverrides={section.onSaveStepOverrides}
      batchStepCopy={stagedStepCopy}
      onBeginSendPipeline={section.onBeginSendPipeline}
      onEndSendPipeline={section.onEndSendPipeline}
      onUnstick={section.onUnstick}
      unstickPending={section.unstickPending}
      onCancel={section.onCancel}
      cancelPending={section.cancelPending}
    />
  );
}
